import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import { useEvent } from './hooks/useEvents';
import { Loader2 } from 'lucide-react';

// Páginas carregadas sob demanda (code splitting)
// Cada rota só baixa seu chunk quando o usuário navegar até ela
const MaintenancePage     = lazy(() => import('./pages/MaintenancePage'));
const LandingPage         = lazy(() => import('./pages/LandingPage'));
const LandingPageV2       = lazy(() => import('./pages/LandingPage_v2'));
const LoginPage           = lazy(() => import('./pages/LoginPage'));
const AuthCallbackPage    = lazy(() => import('./pages/AuthCallbackPage'));
const PilotConsole        = lazy(() => import('./components/console/PilotConsoleV2'));
const EventsPage          = lazy(() => import('./pages/EventsPage'));
const ProfilePage         = lazy(() => import('./pages/ProfilePageV2'));
const HistoryPage         = lazy(() => import('./pages/HistoryPage'));
const TeamsPage           = lazy(() => import('./pages/TeamsPage'));
const TeamCreationPage    = lazy(() => import('./pages/TeamCreationPage'));
const TeamDetailPage      = lazy(() => import('./pages/TeamDetailPage'));
const TeamInternalEventsPage = lazy(() => import('./pages/TeamInternalEventsPage'));
const TeamInternalEventDetailPage = lazy(() => import('./pages/TeamInternalEventDetailPage'));
const LeaguesPage         = lazy(() => import('./pages/LeaguesPage'));
const LeagueCreationPage  = lazy(() => import('./pages/LeagueCreationPage'));
const LeagueProfilePage   = lazy(() => import('./pages/LeagueProfilePage'));
const LeagueProfilePageV2 = lazy(() => import('./pages/LeagueProfilePageV2'));
const LeagueEditPage      = lazy(() => import('./pages/LeagueEditPage'));
const EventDetailsPage    = lazy(() => import('./pages/EventDetailsPage'));
const EventDetailsPageV2  = lazy(() => import('./pages/EventDetailsPageV2'));
const EventFormPage       = lazy(() => import('./pages/EventFormPage'));
const Ams2EventFormPage   = lazy(() => import('./pages/Ams2EventFormPage'));
const EventFormForzaPage  = lazy(() => import('./pages/EventFormForzaPage'));
const EventResultsPage    = lazy(() => import('./pages/EventResultsPage'));
const EventResultsAMS2PageV2 = lazy(() => import('./pages/EventResultsAMS2PageV2'));
const EventResultsForzaPage = lazy(() => import('./pages/EventResultsForzaPage'));
const IncidentReportPage  = lazy(() => import('./pages/IncidentReportPage'));
const StewardsPanel       = lazy(() => import('./pages/StewardsPanel'));
const StewardDashboard    = lazy(() => import('./pages/StewardDashboard'));
const SettingsPage        = lazy(() => import('./pages/SettingsPage'));
const ManagerPage         = lazy(() => import('./pages/ManagerPage'));
const PricingPage         = lazy(() => import('./pages/PricingPage'));
const SubscribePage       = lazy(() => import('./pages/SubscribePage'));
const DonatePage          = lazy(() => import('./pages/DonatePage'));
const RankingPage         = lazy(() => import('./pages/RankingPage'));
const PrivateChatPage     = lazy(() => import('./pages/PrivateChatPage'));
const LeaderboardPage     = lazy(() => import('./pages/LeaderboardPage'));
const MarketplacePage     = lazy(() => import('./pages/MarketplacePage'));
const MarketplaceItemDetailPage = lazy(() => import('./pages/MarketplaceItemDetailPage'));
const MarketplaceSellerDashboardPage = lazy(() => import('./pages/MarketplaceSellerDashboardPage'));
const MarketplaceMyLibraryPage = lazy(() => import('./pages/MarketplaceMyLibraryPage'));
const BroadcastingPage = lazy(() => import('./pages/BroadcastingPage'));
import { EventResultsRivaisPage } from './pages/EventResultsRivaisPage';
const TelemetryPage      = lazy(() => import('./pages/TelemetryPage'));
const MyTelemetryPage    = lazy(() => import('./pages/MyTelemetryPage'));

/**
 * Roteador Inteligente de Edição de Eventos
 * Para eventos Forza: redireciona para EventFormForzaPage (/edit/forza)
 * Para outros jogos: usa EventFormPage padrão
 */
const SmartEventEditPage: React.FC = () => {
  const { slug, eventId } = useParams<{ slug: string; eventId: string }>();
  const { data: event, isLoading } = useEvent(eventId || '');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0a0b0f]">
        <Loader2 className="w-10 h-10 animate-spin text-[#F9CA36]" />
      </div>
    );
  }

  const isForzaEvent =
    event?.game_id === 'forza-motorsport' ||
    event?.games?.slug?.includes('forza') ||
    event?.scoring_template === 'forza_custom' ||
    event?.scoring_template === 'forza';

  if (isForzaEvent) {
    const target = slug
      ? `/leagues/${slug}/events/${eventId}/edit/forza`
      : `/events/${eventId}/edit/forza`;
    return <Navigate to={target} replace />;
  }

  const isAms2Event =
    event?.games?.slug === 'ams2' ||
    event?.games?.short_name?.toLowerCase() === 'ams2' ||
    event?.games?.name?.toLowerCase().includes('automobilista');

  if (isAms2Event) {
    const target = slug
      ? `/leagues/${slug}/events/${eventId}/edit/ams2`
      : `/events/${eventId}/edit/ams2`;
    return <Navigate to={target} replace />;
  }

  return <EventFormPage />;
};

/**
 * Componente Roteador Inteligente de Resultados
 * - Eventos Automobilista 2        → EventResultsAMS2PageV2
 * - Eventos Forza Motorsport        → EventResultsForzaPage  (padrão de produção)
 * - Demais (incluindo Forza Horizon 6 legado) → EventResultsPage
 */
const SmartEventResultsPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const { data: event, isLoading } = useEvent(eventId || '');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0a0b0f]">
        <Loader2 className="w-10 h-10 animate-spin text-[#98c136]" />
      </div>
    );
  }

  // Eventos RIVAIS → tela customizada de hotlap / telemetria
  if (event?.dynamic_type === 'RIVAIS') {
    return <EventResultsRivaisPage />;
  }

  const isAms2 =
    event?.games?.slug === 'ams2' ||
    event?.games?.short_name?.toLowerCase() === 'ams2' ||
    event?.games?.name?.toLowerCase().includes('automobilista') ||
    event?.game_mode === 'ams2' ||
    event?.simulator?.toLowerCase().includes('automobilista') ||
    event?.simulator?.toLowerCase().includes('ams2');

  if (isAms2) {
    return <EventResultsAMS2PageV2 />;
  }

  // Forza Motorsport → tela de lançamento com OCR, splits e suporte a penal/FRR
  const isForza =
    event?.game_id === '39c3bf99-927a-4017-99f3-a22b10d9a861' ||
    event?.games?.slug?.toLowerCase().includes('forza-motorsport') ||
    event?.games?.name?.toLowerCase().includes('forza motorsport') ||
    event?.scoring_template === 'forza_custom' ||
    event?.scoring_template === 'forza';

  if (isForza) {
    return <EventResultsForzaPage />;
  }

  // Fallback: EventResultsPage (Forza Horizon 6 legado e outros jogos genéricos)
  return <EventResultsPage />;
};

/**
 * Guard para rotas fixas de resultados por jogo (results-ams2 / results-forza).
 * Roteia eventos RIVAIS para a tela de hotlap/telemetria, independentemente
 * do jogo — a apuração Rivais é única e vale para qualquer simulador.
 */
const RivaisAwareResults: React.FC<{ fallback: React.ReactElement }> = ({ fallback }) => {
  const { eventId } = useParams<{ eventId: string }>();
  const { data: event, isLoading } = useEvent(eventId || '');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0a0b0f]">
        <Loader2 className="w-10 h-10 animate-spin text-[#98c136]" />
      </div>
    );
  }

  if (event?.dynamic_type === 'RIVAIS') {
    return <EventResultsRivaisPage />;
  }

  return fallback;
};

/**
 * Guard de Rota do Marketplace (vitrine, detalhe e biblioteca)
 * Visível a qualquer usuário autenticado — o cadastro de afiliado/parceiro é
 * justamente a porta de entrada para quem ainda não tem role de criador.
 * Redireciona para /login caso não esteja autenticado.
 */
const MarketplaceRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { emssUser, loading } = useAuth();
  if (loading) return null;
  if (!emssUser) return <Navigate to="/login" replace />;
  return <>{children}</>;
};

/**
 * Guard do Painel do Criador (perfil da loja, produtos e serviços)
 * Exige role 'admin', 'parceiro' ou 'afiliado'. Quem não tem role usa o
 * "Tornar-me Afiliado" na vitrine e aguarda a aprovação no Manager.
 */
const CreatorRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { emssUser, loading } = useAuth();
  if (loading) return null;
  const allowed = emssUser?.roles?.includes('admin') || emssUser?.roles?.includes('parceiro') || emssUser?.roles?.includes('afiliado');
  if (!allowed) return <Navigate to="/marketplace" replace />;
  return <>{children}</>;
};

/**
 * Guard de Rota do Leaderboard
 * Permite acesso apenas a usuários com role 'admin'.
 * Redireciona para /console caso contrário.
 */
const LeaderboardRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { emssUser, loading } = useAuth();
  if (loading) return null;
  if (!emssUser?.roles?.includes('admin')) return <Navigate to="/console" replace />;
  return <>{children}</>;
};

/**
 * Guard de Rota do Broadcasting
 * Permite acesso apenas a usuários com role 'broadcaster' ou 'admin'.
 * Redireciona para /console caso contrário.
 */
const BroadcasterRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { emssUser, loading } = useAuth();
  if (loading) return null;
  const allowed = emssUser?.roles?.includes('admin') || emssUser?.roles?.includes('broadcaster');
  if (!allowed) return <Navigate to="/console" replace />;
  return <>{children}</>;
};

/**
 * Guard de Rota — somente admin
 * Usado nas telas de Agentes e Telemetria.
 * Redireciona para /console caso contrário.
 */
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { emssUser, loading } = useAuth();
  if (loading) return null;
  if (!emssUser?.roles?.includes('admin')) return <Navigate to="/console" replace />;
  return <>{children}</>;
};


// FH6 Pages and layouts (lazy-loaded)
const Fh6ConsoleLayout    = lazy(() => import('./components/layouts/Fh6ConsoleLayout'));
const Fh6PilotConsole     = lazy(() => import('./pages/fh6/console'));
const Fh6EventsPage       = lazy(() => import('./pages/fh6/events'));
const Fh6EventDetailsPage = lazy(() => import('./pages/fh6/event-details'));
const Fh6ProfilePage      = lazy(() => import('./pages/fh6/profile'));
const Fh6RankingPage      = lazy(() => import('./pages/fh6/ranking'));
const Fh6HistoryPage      = lazy(() => import('./pages/fh6/history'));
const Fh6SettingsPage     = lazy(() => import('./pages/fh6/settings'));
const Fh6LeaguesPage      = lazy(() => import('./pages/fh6/leagues'));
const Fh6TeamsPage        = lazy(() => import('./pages/fh6/teams'));
const Fh6TeamCreationPage = lazy(() => import('./pages/fh6/teams-create'));
const Fh6TeamDetailPage   = lazy(() => import('./pages/fh6/team-detail'));
const Fh6LeagueCreatePage = lazy(() => import('./pages/fh6/league-create'));
const Fh6LeagueProfilePage = lazy(() => import('./pages/fh6/league-profile'));
const Fh6LeagueEditPage   = lazy(() => import('./pages/fh6/league-edit'));
const Fh6EventFormPage    = lazy(() => import('./pages/fh6/event-form'));
const Fh6EventResultsPage = lazy(() => import('./pages/fh6/event-results'));
const Fh6IncidentReportPage = lazy(() => import('./pages/fh6/incident-report'));
const Fh6StewardsPanel    = lazy(() => import('./pages/fh6/stewards-panel'));
const Fh6StewardDashboard = lazy(() => import('./pages/fh6/steward-dashboard'));
const Fh6ManagerPage      = lazy(() => import('./pages/fh6/manager'));

// Esses componentes são usados imediatamente na inicialização — mantidos estáticos
import { GameProvider } from './context/GameContext';
import { Fh6Provider } from './context/Fh6Context';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import ConsoleLayout from './components/layouts/ConsoleLayout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import ErrorBoundary from './components/ui/ErrorBoundary';
import { Toaster } from 'react-hot-toast';
import { InstallPrompt } from './components/pwa/InstallPrompt';

/** Skeleton leve exibido enquanto um chunk de página está sendo baixado */
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-[#0a0b0f]">
      <div className="relative w-16 h-16">
        <div
          className="absolute inset-0 border-4 border-white/5 border-t-[#98c136] rounded-full animate-spin"
          style={{ animationDuration: '1s' }}
        />
        <div
          className="absolute inset-3 border-4 border-white/5 border-b-[#1aaffb] rounded-full animate-spin"
          style={{ animationDuration: '1.5s', animationDirection: 'reverse' }}
        />
      </div>
    </div>
  );
}

/**
 * Componente Roteador Dinâmico para a Rota Principal ("/")
 * Exibe a LandingPageV2 por padrão (sistema aberto para todos)
 * ou a MaintenancePage caso o administrador ative o modo de manutenção no ManagerPage.
 */
const MainRoute: React.FC = () => {
  const [isMaintenance, setIsMaintenance] = React.useState(() => {
    return localStorage.getItem('emss_maintenance_mode') === 'true';
  });

  React.useEffect(() => {
    const handleToggle = () => {
      setIsMaintenance(localStorage.getItem('emss_maintenance_mode') === 'true');
    };
    window.addEventListener('emss-maintenance-toggle', handleToggle);
    window.addEventListener('storage', handleToggle);
    return () => {
      window.removeEventListener('emss-maintenance-toggle', handleToggle);
      window.removeEventListener('storage', handleToggle);
    };
  }, []);

  if (isMaintenance) {
    return <MaintenancePage />;
  }

  return <LandingPageV2 />;
};

/**
 * Componente de Redirecionamento Fallback (404)
 * Se o usuário estiver autenticado, redireciona para o Console (/console)
 * Se for visitante, redireciona para a Landing Page (/)
 */
const FallbackRoute: React.FC = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  return <Navigate to={user ? "/console" : "/"} replace />;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AuthProvider>
          <ThemeProvider>
            <ErrorBoundary>
              <Suspense fallback={<PageLoader />}>
                <Routes>

                {/* Public Routes */}
                <Route path="/"              element={<MainRoute />} />
                <Route path="/maintenance"   element={<MaintenancePage />} />
                <Route path="/landing"       element={<LandingPage />} />
                <Route path="/precos"        element={<PricingPage />} />
                <Route path="/doar"          element={<DonatePage />} />
                <Route path="/v2"            element={<LandingPageV2 />} />
                <Route path="/landing-v2"    element={<LandingPageV2 />} />
                <Route path="/login"         element={<LoginPage />} />
                <Route path="/auth/callback" element={<AuthCallbackPage />} />

                {/* Public Console Routes (accessible to all) */}
                <Route element={<GameProvider><ConsoleLayout /></GameProvider>}>
                  <Route path="/events/:eventId" element={<EventDetailsPageV2 />} />
                  <Route path="/events/:eventId/v2" element={<EventDetailsPageV2 />} />
                  <Route path="/leagues/:slug/events/:eventId" element={<EventDetailsPageV2 />} />
                  <Route path="/leagues/:slug/events/:eventId/v2" element={<EventDetailsPageV2 />} />
                </Route>

                {/* Protected Console Routes */}
                <Route element={<ProtectedRoute><GameProvider><ConsoleLayout /></GameProvider></ProtectedRoute>}>
                  <Route path="/assinar"    element={<SubscribePage />} />
                  <Route path="/console"    element={<PilotConsole />} />
                  <Route path="/dashboard"  element={<Navigate to="/console" replace />} />
                  <Route path="/events"     element={<EventsPage />} />
                  <Route path="/profile/:username?" element={<ProfilePage />} />
                  <Route path="/history"    element={<HistoryPage />} />
                  {/* Minha Telemetria — perfil TRACK + relatório PRO (piloto comum) */}
                  <Route path="/minha-telemetria" element={<MyTelemetryPage />} />
                  <Route path="/stewardship" element={<StewardDashboard />} />
                  <Route path="/ranking"    element={<RankingPage />} />
                  <Route path="/settings"   element={<SettingsPage />} />
                  {/* Agentes — redireciona para aba de Configurações do usuário */}
                  <Route path="/agents" element={<Navigate to="/settings?tab=agents" replace />} />
                  {/* Telemetria — somente admin (rota removida por engano no commit 5eab5e62) */}
                  <Route path="/telemetry" element={<AdminRoute><TelemetryPage /></AdminRoute>} />
                  <Route path="/manager"    element={<ManagerPage />} />

                  {/* Teams */}
                  <Route path="/teams"          element={<TeamsPage />} />
                  <Route path="/teams/create"   element={<TeamCreationPage />} />
                  <Route path="/teams/:teamId"  element={<TeamDetailPage />} />
                  <Route path="/teams/:teamId/internal-events" element={<TeamInternalEventsPage />} />
                  <Route path="/teams/:teamId/internal-events/:eventId" element={<TeamInternalEventDetailPage />} />

                  {/* Leagues */}
                  <Route path="/leagues"                                          element={<LeaguesPage />} />
                  <Route path="/leagues/create"                                   element={<LeagueCreationPage />} />
                  <Route path="/leagues/:slug"                                    element={<LeagueProfilePageV2 />} />
                  <Route path="/leagues/:slug/v1"                                 element={<LeagueProfilePage />} />
                  <Route path="/leagues/:slug/edit"                               element={<LeagueEditPage />} />

                  {/* Event Creation */}
                  <Route path="/events/create"                                    element={<EventFormPage />} />
                  <Route path="/events/create/ams2"                               element={<Ams2EventFormPage />} />
                  <Route path="/events/create/forza"                              element={<EventFormForzaPage />} />
                  <Route path="/events/create/forza-test"                          element={<EventFormForzaPage />} />
                  <Route path="/leagues/:slug/events/create"                      element={<EventFormPage />} />
                  <Route path="/leagues/:slug/events/create/ams2"                 element={<Ams2EventFormPage />} />
                  <Route path="/leagues/:slug/events/create/forza"                element={<EventFormForzaPage />} />
                  <Route path="/leagues/:slug/events/create/forza-test"           element={<EventFormForzaPage />} />

                  {/* Event Editing */}
                  <Route path="/events/:eventId/edit"                             element={<SmartEventEditPage />} />
                  <Route path="/events/:eventId/edit/ams2"                        element={<Ams2EventFormPage />} />
                  <Route path="/events/:eventId/edit/forza"                       element={<EventFormForzaPage />} />
                  <Route path="/events/:eventId/edit/forza-test"                   element={<EventFormForzaPage />} />
                  <Route path="/leagues/:slug/events/:eventId/edit"               element={<SmartEventEditPage />} />
                  <Route path="/leagues/:slug/events/:eventId/edit/ams2"          element={<Ams2EventFormPage />} />
                  <Route path="/leagues/:slug/events/:eventId/edit/forza"         element={<EventFormForzaPage />} />
                  <Route path="/leagues/:slug/events/:eventId/edit/forza-test"     element={<EventFormForzaPage />} />

                  {/* Event Results */}
                  <Route path="/events/:eventId/results"                          element={<SmartEventResultsPage />} />
                  <Route path="/events/:eventId/results-ams2"                      element={<RivaisAwareResults fallback={<EventResultsAMS2PageV2 />} />} />
                  <Route path="/events/:eventId/results-forza"                      element={<RivaisAwareResults fallback={<EventResultsForzaPage />} />} />
                  <Route path="/leagues/:slug/events/:eventId/results"            element={<SmartEventResultsPage />} />
                  <Route path="/leagues/:slug/events/:eventId/results-forza"       element={<RivaisAwareResults fallback={<EventResultsForzaPage />} />} />

                  {/* Incidents & Stewards */}
                  <Route path="/events/:eventId/incidents/report"                 element={<IncidentReportPage />} />
                  <Route path="/leagues/:slug/events/:eventId/incidents/report"   element={<IncidentReportPage />} />
                  <Route path="/events/:eventId/stewards"                         element={<StewardsPanel />} />
                  <Route path="/leagues/:slug/stewards"                           element={<StewardsPanel />} />

                  <Route path="/messages/:username?"                               element={<PrivateChatPage />} />
                  {/* Leaderboard — somente admin */}
                  <Route path="/leaderboard" element={<LeaderboardRoute><LeaderboardPage /></LeaderboardRoute>} />

                  {/* Marketplace -- vitrine aberta a autenticados; painel do criador exige role */}
                  <Route path="/marketplace"              element={<MarketplaceRoute><MarketplacePage /></MarketplaceRoute>} />
                  <Route path="/marketplace/items/:id"   element={<MarketplaceRoute><MarketplaceItemDetailPage /></MarketplaceRoute>} />
                  <Route path="/marketplace/seller"       element={<CreatorRoute><MarketplaceSellerDashboardPage /></CreatorRoute>} />
                  <Route path="/marketplace/library"      element={<MarketplaceRoute><MarketplaceMyLibraryPage /></MarketplaceRoute>} />

                  {/* Broadcasting -- narradores e admins */}
                  <Route path="/broadcasting" element={<BroadcasterRoute><BroadcastingPage /></BroadcasterRoute>} />
                </Route>

                {/* Protected FH6 Console Routes */}
                <Route element={<ProtectedRoute><Fh6Provider><Fh6ConsoleLayout /></Fh6Provider></ProtectedRoute>}>
                  <Route path="/fh6/console"    element={<Fh6PilotConsole />} />
                  <Route path="/fh6/events"     element={<Fh6EventsPage />} />
                  <Route path="/fh6/events/:eventId" element={<Fh6EventDetailsPage />} />
                  <Route path="/fh6/leagues/:slug/events/:eventId" element={<Fh6EventDetailsPage />} />
                  <Route path="/fh6/profile/:username?" element={<Fh6ProfilePage />} />
                  <Route path="/fh6/ranking"    element={<Fh6RankingPage />} />
                  <Route path="/fh6/history"    element={<Fh6HistoryPage />} />
                  <Route path="/fh6/settings"   element={<Fh6SettingsPage />} />

                  {/* FH6-themed pages */}
                  <Route path="/fh6/teams"          element={<Fh6TeamsPage />} />
                  <Route path="/fh6/teams/create"   element={<Fh6TeamCreationPage />} />
                  <Route path="/fh6/teams/:teamId"  element={<Fh6TeamDetailPage />} />

                  <Route path="/fh6/leagues"                                          element={<Fh6LeaguesPage />} />
                  <Route path="/fh6/leagues/create"                                   element={<Fh6LeagueCreatePage />} />
                  <Route path="/fh6/leagues/:slug"                                    element={<Fh6LeagueProfilePage />} />
                  <Route path="/fh6/leagues/:slug/edit"                               element={<Fh6LeagueEditPage />} />
                  <Route path="/fh6/events/create"                                    element={<Fh6EventFormPage />} />
                  <Route path="/fh6/leagues/:slug/events/create"                      element={<Fh6EventFormPage />} />
                  <Route path="/fh6/events/:eventId/edit"                             element={<Fh6EventFormPage />} />
                  <Route path="/fh6/leagues/:slug/events/:eventId/edit"               element={<Fh6EventFormPage />} />
                  <Route path="/fh6/events/:eventId/results"                          element={<Fh6EventResultsPage />} />
                  <Route path="/fh6/leagues/:slug/events/:eventId/results"            element={<Fh6EventResultsPage />} />
                  <Route path="/fh6/events/:eventId/incidents/report"                 element={<Fh6IncidentReportPage />} />
                  <Route path="/fh6/leagues/:slug/events/:eventId/incidents/report"   element={<Fh6IncidentReportPage />} />
                  <Route path="/fh6/events/:eventId/stewards"                         element={<Fh6StewardsPanel />} />
                  <Route path="/fh6/leagues/:slug/stewards"                           element={<Fh6StewardsPanel />} />

                  <Route path="/fh6/stewardship" element={<Fh6StewardDashboard />} />
                  <Route path="/fh6/manager"    element={<Fh6ManagerPage />} />
                </Route>

                {/* Fallback */}
                <Route path="*" element={<FallbackRoute />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
            <Toaster
              position="top-right"
              toastOptions={{
                className: 'neo-raised bg-[#1a1d26] text-console-text border border-white/10',
                duration: 4000,
              }}
            />
            <InstallPrompt variant="toast" />
          </ThemeProvider>
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  );
}

export default App;

